import { Cell, Grid, Text } from 'bold-ui'
import { FormSectionAccordion } from 'components/accordion/FormSectionAccordion'
import { SimNaoEnum, TextField } from 'components/form'
import { PesoAutorreferidoRadioGroupField } from 'components/form/field/SobrePesoRadioField'
import { RecordStringSelectField } from 'components/form/final-form/RecordStringSelectField/RecordStringSelectField'
import theme from 'config/theme'
import { css } from 'emotion'
import { SexoEnum } from 'graphql/types.generated'
import React, { Fragment } from 'react'
import { MetaPath } from 'util/metaPath'

import {
  CondicoesSaudeAutorreferidasFormModel,
  doencaCardiacaAutorreferidoRecord,
  doencaRespiratoriaAutorreferidoRecord,
  doencaRinsAutorreferidoRecord,
} from '../model-cidadaoFci'
import { AccordionItem } from './AccordionItem'

interface CondicoesSaudeAutorreferidasSectionProps {
  meta: MetaPath<CondicoesSaudeAutorreferidasFormModel>
  values: CondicoesSaudeAutorreferidasFormModel
  sexo: SexoEnum
}

export const CondicoesSaudeAutorreferidasSection = ({
  meta,
  values,
  sexo,
}: CondicoesSaudeAutorreferidasSectionProps) => {
  return (
    <FormSectionAccordion
      name={meta.openedAccordion}
      title='Condições de saúde autorreferidas'
      uuid='condicoessdesaudeautorreferidas'
      textStyle={css`
        font-size: 1rem;
      `}
    >
      <Grid
        style={css`
          display: flex;
          align-items: center;
          padding: 0.5rem 1rem;
        `}
      >
        {sexo !== SexoEnum.MASCULINO && (
          <Cell size={12} style={styles.cell}>
            <AccordionItem
              name={meta.stGestante}
              title='Está gestante?'
              clearable
              simNaoField
              children={
                <TextField
                  name={meta.maternidadeReferencia}
                  label='Maternidade de referência'
                  title='Maternidade de referência'
                  disabled={values?.stGestante !== SimNaoEnum.SIM}
                />
              }
            />
          </Cell>
        )}
        <Fragment>
          <Cell
            size={7}
            style={css`
              display: flex;
              align-items: center;
              height: 4rem;
            `}
          >
            <Text fontWeight='bold' style={styles.marginText}>
              Sobre seu peso, você se considera?
            </Text>
          </Cell>
          <Cell
            size={5}
            style={css`
              margin-top: 0.25rem;
            `}
          >
            <PesoAutorreferidoRadioGroupField name={meta.autodenominacaoPeso} />
          </Cell>
          <Cell size={12} style={styles.cell} />
        </Fragment>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stAcamado} title='Está acamado?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stDomiciliar} title='Está domiciliado?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem
            name={meta.stPraticasComplementares}
            title='Usa outras práticas integrativas e complementares?'
            clearable
            simNaoField
          />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stFumante} title='É fumante?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stUsoAlcool} title='Faz uso de álcool?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stUsoOutrasDrogas} title='Faz uso de outras drogas?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem
            name={meta.stProblemaSaudeMental}
            title='Teve diagnóstico de algum problema de saúde mental por profissional de saúde?'
            clearable
            simNaoField
          />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stHipertensaoArterial} title='Tem hipertensão arterial?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stDiabetes} title='Tem diabetes?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stCancer} title='Tem ou teve câncer?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stDerrame} title='Teve AVC/derrame?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stInfarto} title='Teve infarto?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stHanseniase} title='Está com hanseníase?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem name={meta.stTuberculose} title='Está com tuberculose?' clearable simNaoField />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem
            name={meta.stDoencaCardiaca}
            title='Teve doença cardíaca/do coração?'
            clearable
            simNaoField
            children={
              <RecordStringSelectField
                name={meta.doencaCardiaca}
                record={doencaCardiacaAutorreferidoRecord}
                label='Indique qual(is)'
                title='Indique qual(is)'
                multiple
                disabled={values?.stDoencaCardiaca !== SimNaoEnum.SIM}
                required={values?.stDoencaCardiaca === SimNaoEnum.SIM}
              />
            }
          />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem
            name={meta.stDoencaRespiratoria}
            title='Tem doença respiratória/no pulmão?'
            clearable
            simNaoField
            children={
              <RecordStringSelectField
                name={meta.doencasRespiratorias}
                record={doencaRespiratoriaAutorreferidoRecord}
                label='Indique qual(is)'
                title='Indique qual(is)'
                multiple
                disabled={values?.stDoencaRespiratoria !== SimNaoEnum.SIM}
                required={values?.stDoencaRespiratoria === SimNaoEnum.SIM}
              />
            }
          />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem
            name={meta.stProblemaRins}
            title='Tem ou teve problemas nos rins?'
            clearable
            simNaoField
            children={
              <RecordStringSelectField
                name={meta.doencaRins}
                record={doencaRinsAutorreferidoRecord}
                label='Indique qual(is)'
                title='Indique qual(is)'
                multiple
                disabled={values?.stProblemaRins !== SimNaoEnum.SIM}
                required={values?.stProblemaRins === SimNaoEnum.SIM}
              />
            }
          />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem
            name={meta.stInternacaoUltimos12Meses}
            title='Teve alguma internação nos últimos 12 meses?'
            clearable
            simNaoField
            children={
              <TextField
                name={meta.causaInternacaoUltimos12Meses}
                label='Por qual causa?'
                title='Por qual causa?'
                disabled={values?.stInternacaoUltimos12Meses !== SimNaoEnum.SIM}
              />
            }
          />
        </Cell>
        <Cell size={12} style={styles.cell}>
          <AccordionItem
            name={meta.stPlantasMedicinais}
            title='Usa plantas medicinais?'
            clearable
            simNaoField
            children={
              <TextField
                name={meta.plantasMedicinais}
                label='Indique qual(is)'
                title='Indique qual(is)'
                disabled={values?.stPlantasMedicinais !== SimNaoEnum.SIM}
              />
            }
          />
        </Cell>
        <Fragment>
          <Cell size={4}>
            <Text fontWeight='bold' style={styles.marginText}>
              Possui alguma outra condição de saúde?
            </Text>
          </Cell>
          <Cell size={4} />
          <Cell size={4}>
            <TextField
              name={meta.condicaoSaude1}
              label='Indique qual(is)'
              title='Indique qual(is)'
              style={styles.marginCondicaoSaude}
            />
            <TextField
              name={meta.condicaoSaude2}
              label='Indique qual(is)'
              title='Indique qual(is)'
              style={styles.marginCondicaoSaude}
            />
            <TextField
              name={meta.condicaoSaude3}
              label='Indique qual(is)'
              title='Indique qual(is)'
              style={styles.marginCondicaoSaude}
            />
          </Cell>
        </Fragment>
      </Grid>
    </FormSectionAccordion>
  )
}

const styles = {
  marginCondicaoSaude: css`
    margin-bottom: 0.5rem;
  `,
  cell: css`
    border-bottom: 1px solid ${theme.pallete.divider};
    padding: 0;
  `,
  marginText: css`
    margin-left: 28px;
  `,
}
